import { useCallback, useEffect, useState } from "react";
import { Copy, Eye, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TermsText } from "@/components/TermsText";
import { adminApi, errMsg, fmtDateTime } from "@/lib/api";

const nextVersion = (n) => (1 + n / 100).toFixed(2);

function History({ list, onView }) {
  if (!list.length) return <p className="text-sm text-muted-foreground" data-testid="terms-history-empty">Nenhuma versão publicada ainda. Enquanto isso, o cadastro mostra um texto padrão.</p>;
  return (
    <ul className="divide-y rounded-lg border" data-testid="terms-history">
      {list.map((t, i) => (
        <li key={t.id} className="flex items-center gap-3 px-4 py-3 text-sm" data-testid={`terms-version-${t.version}`}>
          <span className="font-mono font-semibold">v{t.version}</span>
          <span className="flex-1 text-muted-foreground">{fmtDateTime(t.created_at)}</span>
          {i === 0 && <span className="rounded bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">Em uso</span>}
          <Button variant="outline" size="sm" onClick={() => onView(t)} data-testid={`terms-view-${t.version}`}><Eye className="mr-1.5 h-4 w-4" />Ver</Button>
        </li>
      ))}
    </ul>
  );
}

export function TermsTab() {
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const load = useCallback(() => adminApi.get("/terms").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const publish = async () => {
    if (!window.confirm(`Publicar a versão ${nextVersion(list.length)}? Os novos cadastros passam a aceitar esta versão.`)) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post("/terms", { text });
      toast.success(`Versão ${data.version} publicada`);
      setText("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="card-surface space-y-3 p-5" data-testid="terms-editor">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="eyebrow">Nova versão dos termos de uso e política de privacidade</p>
          {list[0] && <Button variant="ghost" size="sm" onClick={() => setText(list[0].text)} data-testid="terms-copy-current"><Copy className="mr-1.5 h-4 w-4" />Começar da versão atual</Button>}
        </div>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={16} placeholder="Cole aqui o texto completo dos termos de uso e da política de privacidade" data-testid="terms-textarea" />
        <p className="text-xs text-muted-foreground">A data e a versão entram sozinhas no final do texto. A versão anterior fica guardada no histórico.</p>
        <Button onClick={publish} disabled={busy || text.trim().length < 20} data-testid="terms-publish-btn"><Send className="mr-2 h-4 w-4" />Publicar versão {nextVersion(list.length)}</Button>
      </div>
      <div className="card-surface space-y-3 p-5">
        <p className="eyebrow">Histórico de versões</p>
        <History list={list} onView={setView} />
      </div>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="terms-view-modal">
          <DialogHeader><DialogTitle>Termos de uso e política de privacidade</DialogTitle></DialogHeader>
          <TermsText doc={view} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
